import { useState } from "react";

interface Props {
  onClose: () => void;
  onSuccess: () => void;
}

const CATEGORIES = [
  "Electronics", "Fashion", "Home & Living", "Beauty & Skincare",
  "Sports & Fitness", "Food & Beverage", "Toys & Kids", "Automotive", "Industrial", "Other",
];

const COUNTRIES = [
  "United States", "United Kingdom", "Canada", "Australia", "Germany", "France",
  "Japan", "South Korea", "Singapore", "UAE", "Saudi Arabia", "Brazil", "Mexico",
  "India", "Netherlands", "Italy", "Spain", "Sweden", "New Zealand", "South Africa",
  "Turkey", "Russia", "Argentina", "Chile", "Nigeria", "Kenya", "Thailand",
  "Vietnam", "Indonesia", "Malaysia", "Philippines",
];

export default function SubmitRequirementForm({ onClose, onSuccess }: Props) {
  const [form, setForm] = useState({
    buyerName: "",
    company: "",
    email: "",
    whatsapp: "",
    country: "",
    category: "",
    title: "",
    quantity: "",
    budget: "",
    targetDelivery: "",
    description: "",
    agreePrivacy: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = (field: string, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.buyerName.trim()) e.buyerName = "Required";
    if (!form.email.trim()) e.email = "Required";
    if (!form.whatsapp.trim()) e.whatsapp = "Required";
    if (!form.country) e.country = "Required";
    if (!form.category) e.category = "Required";
    if (!form.title.trim()) e.title = "Required";
    if (!form.quantity.trim()) e.quantity = "Required";
    if (!form.description.trim()) e.description = "Required";
    if (form.description.length > 500) e.description = "Max 500 characters";
    if (!form.agreePrivacy) e.agreePrivacy = "You must agree to continue";
    return e;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(form).forEach(([k, v]) => body.append(k, String(v)));
      await fetch("https://readdy.ai/api/form/d7e0ea4kpdh0a4ae08cg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      onSuccess();
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-2xl my-4" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div>
            <h2 className="text-base font-extrabold text-gray-900">Submit Bulk Order Requirement</h2>
            <p className="text-xs text-gray-400 mt-0.5">Free to submit · Contact info hidden from public · Only verified factory members can see it</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-full hover:bg-gray-100">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <form data-readdy-form id="buyer-requirement-form" onSubmit={handleSubmit} className="px-6 py-5 space-y-4 max-h-[78vh] overflow-y-auto">
          {/* Privacy Notice */}
          <div className="flex items-start gap-3 bg-green-50 border border-green-100 rounded-xl p-4">
            <i className="ri-shield-check-fill text-green-500 text-lg flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-green-800 mb-0.5">Your Privacy is Protected</p>
              <p className="text-xs text-green-700 leading-relaxed">
                Your WhatsApp number and email are <strong>completely hidden from the public</strong>. Only paid membership verified factories ($8/mo) can view your contact details and send you quotations.
              </p>
            </div>
          </div>

          {/* Buyer Info */}
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Your Contact Information</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Full Name *</label>
              <input name="buyerName" type="text" value={form.buyerName} onChange={(e) => set("buyerName", e.target.value)} placeholder="John Smith" className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none focus:border-orange-400 transition-colors ${errors.buyerName ? "border-red-400" : "border-gray-200"}`} />
              {errors.buyerName && <p className="text-xs text-red-500 mt-1">{errors.buyerName}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Company Name</label>
              <input name="company" type="text" value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="ABC Trading Co." className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-orange-400 transition-colors" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                Email Address * <span className="text-green-600 font-normal">(hidden from public)</span>
              </label>
              <div className="relative">
                <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input name="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@company.com" className={`w-full pl-9 pr-3 py-2.5 border rounded-lg text-sm outline-none focus:border-orange-400 transition-colors ${errors.email ? "border-red-400" : "border-gray-200"}`} />
              </div>
              {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                WhatsApp Number * <span className="text-green-600 font-normal">(hidden from public)</span>
              </label>
              <div className="relative">
                <i className="ri-whatsapp-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input name="whatsapp" type="text" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+1 555 000 0000" className={`w-full pl-9 pr-3 py-2.5 border rounded-lg text-sm outline-none focus:border-orange-400 transition-colors ${errors.whatsapp ? "border-red-400" : "border-gray-200"}`} />
              </div>
              {errors.whatsapp && <p className="text-xs text-red-500 mt-1">{errors.whatsapp}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Your Country *</label>
              <select name="country" value={form.country} onChange={(e) => set("country", e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none focus:border-orange-400 bg-white cursor-pointer transition-colors ${errors.country ? "border-red-400" : "border-gray-200"}`}>
                <option value="">Select your country</option>
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              {errors.country && <p className="text-xs text-red-500 mt-1">{errors.country}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Product Category *</label>
              <select name="category" value={form.category} onChange={(e) => set("category", e.target.value)} className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none focus:border-orange-400 bg-white cursor-pointer transition-colors ${errors.category ? "border-red-400" : "border-gray-200"}`}>
                <option value="">Select category</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              {errors.category && <p className="text-xs text-red-500 mt-1">{errors.category}</p>}
            </div>
          </div>

          {/* Requirement Details */}
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide pt-2">Requirement Details</p>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Requirement Title *</label>
            <input name="title" type="text" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Wireless Earbuds — 5,000 Units Bulk Order" className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none focus:border-orange-400 transition-colors ${errors.title ? "border-red-400" : "border-gray-200"}`} />
            {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Order Quantity *</label>
              <input name="quantity" type="text" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} placeholder="e.g. 5,000 units" className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none focus:border-orange-400 transition-colors ${errors.quantity ? "border-red-400" : "border-gray-200"}`} />
              {errors.quantity && <p className="text-xs text-red-500 mt-1">{errors.quantity}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Budget Range</label>
              <input name="budget" type="text" value={form.budget} onChange={(e) => set("budget", e.target.value)} placeholder="e.g. $50,000 – $80,000" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-orange-400 transition-colors" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Target Delivery</label>
              <input name="targetDelivery" type="text" value={form.targetDelivery} onChange={(e) => set("targetDelivery", e.target.value)} placeholder="e.g. June 2026" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-orange-400 transition-colors" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Detailed Requirements * <span className="text-gray-400 font-normal">({form.description.length}/500)</span>
            </label>
            <textarea
              name="description"
              value={form.description}
              onChange={(e) => {
                if (e.target.value.length <= 500) set("description", e.target.value);
              }}
              maxLength={500}
              rows={4}
              placeholder="Describe your product requirements — specifications, certifications, packaging, branding, OEM/ODM needs, etc."
              className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none focus:border-orange-400 transition-colors resize-none ${errors.description ? "border-red-400" : "border-gray-200"}`}
            />
            {errors.description && <p className="text-xs text-red-500 mt-1">{errors.description}</p>}
          </div>

          {/* Privacy Agreement */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="agreePrivacy"
                checked={form.agreePrivacy}
                onChange={(e) => set("agreePrivacy", e.target.checked)}
                className="mt-0.5 accent-orange-500 cursor-pointer w-4 h-4 flex-shrink-0"
              />
              <span className="text-xs text-gray-600 leading-relaxed">
                I agree that my contact information (email &amp; WhatsApp) will be kept private and only shared with <strong>paid membership verified factories</strong> on StarBookPrime. I consent to receiving quotations from verified factories via email and WhatsApp.
              </span>
            </label>
            {errors.agreePrivacy && <p className="text-xs text-red-500 mt-2">{errors.agreePrivacy}</p>}
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3.5 bg-orange-500 text-white text-sm font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-70"
          >
            {submitting ? (
              <><i className="ri-loader-4-line animate-spin" /> Submitting...</>
            ) : (
              <><i className="ri-send-plane-line" /> Submit Requirement — Free</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
